import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Phone, MessageCircle, Pencil, Trophy, Building2, Trash2, ExternalLink, Upload, CircleCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS_MAP = {
  new:         { label: "New Lead",    pill: "bg-blue-50 text-blue-700 border-blue-200" },
  contacted:   { label: "Contacted",   pill: "bg-sky-50 text-sky-700 border-sky-200" },
  qualified:   { label: "Qualified",   pill: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  meeting:     { label: "Meeting",     pill: "bg-violet-50 text-violet-700 border-violet-200" },
  quotation:   { label: "Quotation",   pill: "bg-amber-50 text-amber-700 border-amber-200" },
  negotiation: { label: "Negotiation", pill: "bg-orange-50 text-orange-700 border-orange-200" },
  won:         { label: "Won",         pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  lost:        { label: "Lost",        pill: "bg-rose-50 text-rose-700 border-rose-200" },
};
const PIPELINE = ["new","contacted","qualified","meeting","quotation","negotiation","won"];
const PRI_MAP = { high:"bg-red-100 text-red-700", medium:"bg-yellow-100 text-yellow-800", low:"bg-slate-100 text-slate-700" };
const ACT_ICONS = { created:"fa-plus", status_change:"fa-shuffle", note:"fa-note-sticky",
  meeting_scheduled:"fa-calendar-plus", meeting_done:"fa-calendar-check",
  document_upload:"fa-file-arrow-up", won:"fa-trophy", lost:"fa-xmark",
  call:"fa-phone", email:"fa-envelope", edit:"fa-pen", assignment:"fa-user-tag" };

const TABS = ["Info","Activity","Notes","Meetings","Documents","Status History"];

export default function LeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [tab, setTab] = useState("Info");
  const [noteText, setNoteText] = useState("");
  const [meetingForm, setMeetingForm] = useState({ scheduled_at:"", duration_mins:30, meeting_type:"call", location:"", agenda:"" });
  const [docTitle, setDocTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [l, a, m, d] = await Promise.all([
        api.get(`/leads/${id}`), api.get(`/leads/${id}/activities`),
        api.get(`/leads/${id}/meetings`), api.get(`/leads/${id}/documents`),
      ]);
      setLead(l.data); setActivities(a.data); setMeetings(m.data); setDocuments(d.data);
    } catch { toast.error("Failed to load lead"); nav("/leads"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/leads/${id}/notes`, { text: noteText });
    setNoteText(""); toast.success("Note added"); load();
  };
  const scheduleMeeting = async () => {
    if (!meetingForm.scheduled_at) return toast.error("Date/time required");
    await api.post(`/leads/${id}/meetings`, meetingForm);
    setMeetingForm({ scheduled_at:"", duration_mins:30, meeting_type:"call", location:"", agenda:"" });
    toast.success("Meeting scheduled"); load();
  };
  const updateMeeting = async (m, status, outcome="") => {
    await api.patch(`/leads/${id}/meetings/${m.id}`, { status, outcome });
    toast.success("Meeting updated"); load();
  };
  const uploadDoc = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("title", docTitle);
      await api.post(`/leads/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); setDocTitle(""); load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const deleteDoc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/leads/${id}/documents/${docId}`); toast.success("Deleted"); load();
  };
  const convert = async () => {
    if (!window.confirm("Convert this lead to a client?")) return;
    await api.post(`/leads/${id}/convert`); toast.success("Converted to client"); load();
  };
  const del = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    await api.delete(`/leads/${id}`); toast.success("Deleted"); nav("/leads");
  };

  if (!lead) return <div className="text-muted-foreground p-6">Loading lead…</div>;
  const sMeta = STATUS_MAP[lead.status] || STATUS_MAP.new;
  const curIdx = PIPELINE.indexOf(lead.status);
  const notes = activities.filter(a => a.activity_type === "note");
  const history = activities.filter(a => ["created","status_change","won","lost"].includes(a.activity_type));

  return (
    <div className="space-y-4" data-testid="lead-detail">
      <Link to="/leads" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> All Leads</Link>

      {/* Header */}
      <div className="rounded-2xl p-6 bg-card border border-border/60" data-testid="lead-detail-header">
        <div className="flex justify-between items-start gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-3xl font-semibold">{lead.name}</h1>
              <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full border ${sMeta.pill}`}>{sMeta.label}</span>
              <span className={`text-xs px-2 py-0.5 rounded font-medium ${PRI_MAP[lead.priority]||PRI_MAP.medium}`}>{(lead.priority||"medium").toUpperCase()} Priority</span>
            </div>
            {lead.company && <div className="text-muted-foreground mt-1 flex items-center gap-1.5 text-sm"><Building2 className="w-3.5 h-3.5" />{lead.company}</div>}
            <div className="text-xs text-muted-foreground mt-1"><span className="font-mono">{lead.lead_code || "—"}</span> · Created {new Date(lead.created_at).toLocaleDateString()}</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {lead.phone && <a href={`https://wa.me/${lead.phone.replace(/\D/g,"")}?text=Hi ${encodeURIComponent(lead.name)}`} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center px-3 py-1.5 rounded-md border border-border/60 hover:bg-muted text-sm"><MessageCircle className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />WhatsApp</a>}
            {lead.phone && <a href={`tel:${lead.phone}`} className="inline-flex items-center px-3 py-1.5 rounded-md border border-border/60 hover:bg-muted text-sm"><Phone className="w-3.5 h-3.5 mr-1.5" />Call</a>}
            {lead.status !== "won" && lead.status !== "lost" && <Button onClick={convert} className="bg-primary text-primary-foreground" data-testid="convert-lead-btn"><Trophy className="w-4 h-4 mr-1.5" />Convert to Client</Button>}
            <Button variant="outline" onClick={del} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
          </div>
        </div>

        {/* Pipeline stepper */}
        <div className="mt-6 pt-5 border-t border-border/50 overflow-x-auto" data-testid="pipeline-stepper">
          <div className="flex items-center gap-1 min-w-max">
            {PIPELINE.map((st, i) => {
              const done = curIdx > i, active = curIdx === i;
              const m = STATUS_MAP[st];
              return (
                <div key={st} className="flex items-center gap-1">
                  <div className="flex items-center gap-2 shrink-0">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${done ? "bg-emerald-500 text-white" : active ? "bg-orange-500 text-white ring-4 ring-orange-500/20" : "bg-muted text-muted-foreground border border-border"}`}>{done ? <CircleCheck className="w-3.5 h-3.5" /> : i+1}</div>
                    <span className={`text-xs whitespace-nowrap ${active ? "font-semibold" : done ? "text-emerald-600" : "text-muted-foreground"}`}>{m.label}</span>
                  </div>
                  {i < PIPELINE.length-1 && <div className={`w-8 border-t-2 border-dashed ${done ? "border-emerald-400" : "border-border"}`} />}
                </div>
              );
            })}
            {lead.status === "lost" && <div className="ml-3 text-[10px] uppercase px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200">Lost</div>}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="rounded-2xl bg-card border border-border/60 overflow-hidden">
        <div className="flex border-b border-border/50 px-4 overflow-x-auto" data-testid="detail-tabs">
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
                    className={`py-3 px-4 text-sm whitespace-nowrap border-b-2 -mb-px ${tab === t ? "border-orange-500 font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                    data-testid={`tab-${t.toLowerCase().replace(/\s/g,"-")}`}>
              {t}
              {t === "Activity" && ` (${activities.length})`}
              {t === "Meetings" && ` (${meetings.length})`}
              {t === "Documents" && ` (${documents.length})`}
            </button>
          ))}
        </div>

        {tab === "Info" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
            <InfoBlock title="Customer">
              <Field label="Name" value={lead.name} />
              <Field label="Company" value={lead.company || "—"} />
              <Field label="Phone" value={lead.phone || "—"} />
              <Field label="Email" value={lead.email || "—"} />
              <Field label="Address" value={lead.address || "—"} />
            </InfoBlock>
            <InfoBlock title="Enquiry">
              <Field label="Source" value={lead.source || "—"} />
              <Field label="Service Required" value={lead.service_required || "—"} />
              <Field label="Project Type" value={lead.project_type || "—"} />
              <Field label="Budget" value={`₹${Number(lead.budget||0).toLocaleString("en-IN")}`} />
              <Field label="Priority" value={<span className={`text-xs px-2 py-0.5 rounded ${PRI_MAP[lead.priority]||PRI_MAP.medium}`}>{(lead.priority||"medium").toUpperCase()}</span>} />
              <Field label="Follow-up" value={lead.follow_up_date || "—"} />
            </InfoBlock>
            {lead.remarks && <div className="md:col-span-2"><div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Remarks</div><div className="p-3 rounded-lg bg-muted/40 text-sm whitespace-pre-wrap">{lead.remarks}</div></div>}
          </div>
        )}

        {tab === "Activity" && (
          <div className="p-6" data-testid="activity-timeline">
            <div className="relative pl-4">
              <div className="absolute left-5 top-2 bottom-2 w-0.5 bg-border" />
              {activities.map(a => (
                <div key={a.id} className="relative pl-10 pb-4">
                  <div className="absolute left-0 top-0.5 w-6 h-6 rounded-full bg-background border-2 border-primary flex items-center justify-center text-primary text-[10px]"><i className={`fa-solid ${ACT_ICONS[a.activity_type] || 'fa-circle'}`}></i></div>
                  <div className="text-sm">{a.description}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{a.user_name || "System"} · {new Date(a.created_at).toLocaleString()}</div>
                </div>
              ))}
              {!activities.length && <div className="text-sm text-muted-foreground">No activity yet.</div>}
            </div>
          </div>
        )}

        {tab === "Notes" && (
          <div className="p-6">
            <div className="flex gap-2 mb-4">
              <Input placeholder="Add a note about this lead…" value={noteText} onChange={e => setNoteText(e.target.value)} onKeyDown={e => e.key === "Enter" && addNote()} data-testid="note-input" />
              <Button onClick={addNote} className="bg-primary text-primary-foreground whitespace-nowrap" data-testid="add-note-btn">Add Note</Button>
            </div>
            <div className="space-y-2">
              {notes.map(n => (
                <div key={n.id} className="p-3 rounded-lg bg-muted/40">
                  <div className="text-sm">{n.description}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">{n.user_name || "You"} · {new Date(n.created_at).toLocaleString()}</div>
                </div>
              ))}
              {!notes.length && <div className="text-sm text-muted-foreground">No notes yet.</div>}
            </div>
          </div>
        )}

        {tab === "Meetings" && (
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-5 p-4 rounded-lg bg-muted/40">
              <Input type="datetime-local" value={meetingForm.scheduled_at} onChange={e => setMeetingForm({...meetingForm, scheduled_at: e.target.value})} className="col-span-2 md:col-span-2" />
              <Input type="number" value={meetingForm.duration_mins} onChange={e => setMeetingForm({...meetingForm, duration_mins: Number(e.target.value)})} placeholder="Mins" />
              <Select value={meetingForm.meeting_type} onValueChange={v => setMeetingForm({...meetingForm, meeting_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["call","video","office","site_visit","whatsapp"].map(t => <SelectItem key={t} value={t}>{t.replace("_"," ")}</SelectItem>)}</SelectContent></Select>
              <Input placeholder="Location / link" value={meetingForm.location} onChange={e => setMeetingForm({...meetingForm, location: e.target.value})} />
              <Button onClick={scheduleMeeting} className="bg-primary text-primary-foreground" data-testid="schedule-meeting-btn">Schedule</Button>
              <Input placeholder="Agenda" value={meetingForm.agenda} onChange={e => setMeetingForm({...meetingForm, agenda: e.target.value})} className="col-span-2 md:col-span-6" />
            </div>
            <div className="space-y-2">
              {meetings.map(m => (
                <div key={m.id} className="rounded-lg border border-border/50 p-4">
                  <div className="flex justify-between items-start gap-3 flex-wrap">
                    <div>
                      <div className="text-sm font-medium">{m.agenda || "Meeting"}</div>
                      <div className="text-xs text-muted-foreground mt-1">{new Date(m.scheduled_at).toLocaleString()} · {m.duration_mins}min · {(m.meeting_type||"").replace("_"," ")}{m.location ? ` · ${m.location}` : ""}</div>
                      {m.outcome && <div className="text-xs mt-2 p-2 rounded bg-muted/40"><strong>Outcome:</strong> {m.outcome}</div>}
                    </div>
                    <span className={`text-[10px] uppercase px-2 py-1 rounded-full ${m.status==="scheduled"?"bg-amber-50 text-amber-700":m.status==="completed"?"bg-emerald-50 text-emerald-700":"bg-slate-100 text-slate-600"}`}>{m.status}</span>
                  </div>
                  {m.status === "scheduled" && (
                    <div className="mt-3 flex gap-2 items-center">
                      <Button size="sm" variant="outline" onClick={() => { const o = window.prompt("Outcome / notes"); if (o !== null) updateMeeting(m, "completed", o); }}>Mark completed</Button>
                      <Button size="sm" variant="outline" onClick={() => updateMeeting(m, "cancelled")}>Cancel</Button>
                    </div>
                  )}
                </div>
              ))}
              {!meetings.length && <div className="text-sm text-muted-foreground">No meetings scheduled.</div>}
            </div>
          </div>
        )}

        {tab === "Documents" && (
          <div className="p-6">
            <div className="flex gap-2 mb-5 p-4 rounded-lg bg-muted/40 flex-wrap md:flex-nowrap">
              <Input placeholder="Title (optional)" value={docTitle} onChange={e => setDocTitle(e.target.value)} className="md:max-w-xs" />
              <input ref={fileRef} type="file" onChange={uploadDoc} className="flex-1 text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-primary file:text-primary-foreground hover:file:bg-primary/90" data-testid="upload-doc-input" />
              {uploading && <span className="text-xs text-muted-foreground self-center">Uploading…</span>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {documents.map(d => (
                <div key={d.id} className="rounded-lg border border-border/50 p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded bg-muted flex items-center justify-center"><i className="fa-solid fa-file text-lg text-muted-foreground"></i></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{d.title || d.original_name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{d.original_name} · {Math.round((d.file_size||0)/1024)} KB · {new Date(d.created_at).toLocaleDateString()}</div>
                  </div>
                  <a href={d.file_path} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-muted" title="Open"><ExternalLink className="w-3.5 h-3.5" /></a>
                  <button onClick={() => deleteDoc(d.id)} className="p-1.5 rounded hover:bg-destructive/10 text-destructive" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              {!documents.length && <div className="text-sm text-muted-foreground md:col-span-2">No documents uploaded.</div>}
            </div>
          </div>
        )}

        {tab === "Status History" && (
          <div className="p-6 space-y-2" data-testid="status-history">
            {history.map(h => (
              <div key={h.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/40">
                {h.activity_type === "status_change" ? (
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`text-[10px] uppercase px-2 py-1 rounded-full border ${STATUS_MAP[h.from_status]?.pill || "bg-muted"}`}>{STATUS_MAP[h.from_status]?.label || h.from_status}</span>
                    <i className="fa-solid fa-arrow-right text-muted-foreground text-xs"></i>
                    <span className={`text-[10px] uppercase px-2 py-1 rounded-full border ${STATUS_MAP[h.to_status]?.pill || "bg-muted"}`}>{STATUS_MAP[h.to_status]?.label || h.to_status}</span>
                  </div>
                ) : <div className="text-sm">{h.description}</div>}
                <div className="ml-auto text-[11px] text-muted-foreground">{h.user_name || "System"} · {new Date(h.created_at).toLocaleString()}</div>
              </div>
            ))}
            {!history.length && <div className="text-sm text-muted-foreground">No status changes yet.</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function InfoBlock({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium mt-0.5">{value}</div>
    </div>
  );
}
